import { useMemo } from 'react';

import { Form, Formik } from 'formik';
import { object, number } from 'yup';

import Button from 'components/common/forms/Button';
import ButtonLoading from 'components/common/forms/ButtonLoading';
import FormInput from 'components/common/forms/FormInput';
import Modal, { ModalHeader } from 'components/common/Modal';
import { useAddOrganizationCredits } from 'hooks/backend/billing';
import { showErrorMessage, showSuccessMessage } from 'util/notifications';
import { formatDollars, parseCurrencyStringToDecimal } from 'util/currency';
import { OrganizationCredits } from 'services/backend/billing';
import { OrganizationV2 } from 'types/domainModels';
import { Decimal } from 'decimal.js-light';

interface AddCreditsFormData {
  amount: number | '';
  description: string | '';
}

const MIN_CREDITS_TO_ADD = 1;
const MAX_CREDITS_TO_ADD = 50000;

const AddCreditsSchema = object().shape({
  amount: number()
    .typeError('Please enter a valid amount.')
    .required('Please enter an amount.')
    .min(MIN_CREDITS_TO_ADD, 'Minimum addition amount is $1.00')
    .max(MAX_CREDITS_TO_ADD, 'Maximum addition amount is $50,000.00'),
});

const ROW_STYLING = 'flex justify-between py-2 text-sm';

const TotalAfterAddingAmount = ({
  amount,
  currentCreditAmount,
}: {
  amount: number | '';
  currentCreditAmount: string | undefined;
}) => {
  const total = useMemo(
    () =>
      formatDollars(
        parseCurrencyStringToDecimal(currentCreditAmount ?? '')
          .plus(amount || 0)
          .toNumber(),
      ),
    [currentCreditAmount, amount],
  );

  return <dd className="font-medium text-gray-d-900">{total}</dd>;
};

export const AddCreditsModal = ({
  onCloseModal,
  organization,
  orgCredits,
}: {
  onCloseModal(): void;
  organization: OrganizationV2 | undefined;
  orgCredits: OrganizationCredits | undefined;
}): JSX.Element => {
  const { isPending: isAddingCredits, mutate: addOrganizationCredits } =
    useAddOrganizationCredits({
      onError: (err) => {
        showErrorMessage({ err });
      },
      onSuccess: (_data, variables) => {
        showSuccessMessage(
          `Added ${formatDollars(variables.creditAmount.div(100).toNumber())} credits to ${organization?.name}.`,
        );
        onCloseModal();
      },
    });

  return (
    <Modal
      header={
        <ModalHeader onClickClose={onCloseModal}>Add credits</ModalHeader>
      }
      onCloseModal={onCloseModal}
      position="top"
    >
      <Formik<AddCreditsFormData>
        initialValues={{
          amount: '',
          description: 'Manual addition of credits by admin',
        }}
        onSubmit={async (formData) => {
          if (!organization) {
            showErrorMessage({
              message:
                'No organization selected. Please close this modal and select and organization.',
            });
            return;
          }
          addOrganizationCredits({
            organizationId: organization.id,
            creditAmount: formData.amount
              ? new Decimal(Math.round(Number(formData.amount) * 100))
              : new Decimal(0),
            description: formData.description || undefined,
          });
        }}
        validateOnChange={false}
        validationSchema={AddCreditsSchema}
      >
        {({ values }) => {
          const isAmountInvalid = !AddCreditsSchema.isValidSync({
            amount: values.amount,
          });

          return (
            <Form className="space-y-4">
              <dl className="divide-y divide-gray-d-200">
                <div className={ROW_STYLING}>
                  <dt className="text-gray-d-600">Organization</dt>
                  <dd className="font-medium text-gray-d-900">
                    {organization?.name}
                  </dd>
                </div>
                <div className={ROW_STYLING}>
                  <dt className="text-gray-d-600">Current credits</dt>
                  <dd className="font-medium text-gray-d-900">
                    {orgCredits?.totalCreditAmount ?? ''}
                  </dd>
                </div>
                <div className={ROW_STYLING}>
                  <dt className="text-gray-d-600">Credits to add</dt>
                  <dd>
                    <FormInput
                      icon={<span className="text-gray-d-500">$</span>}
                      iconPlacement="leading"
                      id="amount"
                      labelFor="amount"
                      name="amount"
                      size="md"
                      step="0.01"
                      type="number"
                    />
                  </dd>
                </div>
                <div className={ROW_STYLING}>
                  <dt className="text-gray-d-600">Total after adding</dt>
                  <TotalAfterAddingAmount
                    amount={values.amount}
                    currentCreditAmount={orgCredits?.totalCreditAmount}
                  />
                </div>
                <div className={ROW_STYLING}>
                  <dt className="text-gray-d-600">Description</dt>
                  <dd className="w-96 ml-4">
                    <FormInput
                      id="description"
                      labelFor="description"
                      name="description"
                      size="md"
                      type="text"
                    />
                  </dd>
                </div>
              </dl>
              <div className="flex gap-4">
                <Button
                  grow
                  hierarchy="secondary-gray"
                  onClick={onCloseModal}
                  size="lg"
                  type="button"
                >
                  Cancel
                </Button>
                <ButtonLoading
                  disabled={isAmountInvalid}
                  grow
                  hierarchy="primary"
                  isLoading={isAddingCredits}
                  size="lg"
                  type="submit"
                >
                  Add Credits
                </ButtonLoading>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
